import React, { useState, useEffect } from "react";
import "./PersonalInfoForm.scss";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { Divider } from "primereact/divider";
import { ProfileFormData } from "@features/settings/model/types";
import { useUnit } from "effector-react";
import { $countriesTerms } from "@/core/store/terms";
import { Term } from "@/core/store/terms/types";
import { countryPhoneCodes } from "./countryCodes.json";
import { PhotoUpload } from "./PhotoUpload";
import { Control, Controller, FieldErrors, useWatch } from "react-hook-form";

interface PersonalInfoFormProps {
  control: Control<ProfileFormData>;
  errors: FieldErrors<ProfileFormData>;
  validationErrors?: string[];
}

export const PersonalInfoForm: React.FC<PersonalInfoFormProps> = ({
  control,
  errors,
  validationErrors = [],
}) => {
  const countriesTerms = useUnit($countriesTerms);

  // Watch the userPictureUrl for display
  const userPictureUrl = useWatch({
    control,
    name: "userPictureUrl",
  });

  // Watch phone number to sync country code
  const phoneNumber = useWatch({
    control,
    name: "phoneNumber",
  });

  // State for selected country phone code — default to Greece
  const [selectedCountryCode, setSelectedCountryCode] = useState<string | null>(
    "GR",
  );

  // Helper function to extract country code from phone number
  const extractCountryCode = (phone: string) => {
    if (!phone) return null;

    // console.log("Extracting country code from:", phone);

    // Remove any spaces first
    const cleanPhone = phone.replace(/\s/g, "");

    // Try to match against known country codes (longest first)
    // Sort country codes by length (descending) to match longest first
    const sortedCountryCodes = [...countryPhoneCodes].sort(
      (a, b) => b.code.length - a.code.length,
    );

    for (const country of sortedCountryCodes) {
      if (cleanPhone.startsWith(country.code)) {
        // console.log(
          // "Matched country code:",
          // country.code,
          // "for country:",
          // country.value,
        // );
        return country.value;
      }
    }

    // console.log("No matching country code found");
    return null;
  };

  // Sync country code when phone number changes (only update if a match is found)
  useEffect(() => {
    const extractedCode = extractCountryCode(phoneNumber);
    if (extractedCode) {
      setSelectedCountryCode(extractedCode);
    }
  }, [phoneNumber]);

  // Transform terms data to dropdown options
  const countries = countriesTerms.data.map((term: Term) => ({
    label: term.name,
    value: parseInt(term.tid),
    tid: term.tid,
    name: term.name,
  }));

  const countryTemplate = (option: any) => {
    if (!option) return null;
    return (
      <div
        className="country-option"
        style={{ width: "100%", overflow: "hidden" }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "block",
            width: "100%",
          }}
        >
          {option.label}
        </span>
      </div>
    );
  };

  return (
    <>
      <div className="form-row">
        <div className="form-group">
          <label
            htmlFor="firstName"
            style={{
              color:
                validationErrors.includes("firstName") ||
                validationErrors.includes("lastName")
                  ? "#ef4444"
                  : undefined,
            }}
          >
            Name
          </label>
          <div className="p-inputgroup name-inputs">
            <div style={{ flex: 1 }}>
              <Controller
                name="firstName"
                control={control}
                rules={{ required: "First name is required" }}
                render={({ field }) => (
                  <InputText
                    id="firstName"
                    {...field}
                    placeholder="First name"
                    className={errors.firstName ? "p-invalid" : ""}
                    style={{ width: "100%" }}
                  />
                )}
              />
              {errors.firstName && (
                <small
                  className="p-error"
                  style={{ display: "block", marginTop: "0.25rem" }}
                >
                  {errors.firstName.message}
                </small>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <Controller
                name="lastName"
                control={control}
                rules={{ required: "Last name is required" }}
                render={({ field }) => (
                  <InputText
                    id="lastName"
                    {...field}
                    placeholder="Last name"
                    className={errors.lastName ? "p-invalid" : ""}
                    style={{ width: "100%" }}
                  />
                )}
              />
              {errors.lastName && (
                <small
                  className="p-error"
                  style={{ display: "block", marginTop: "0.25rem" }}
                >
                  {errors.lastName.message}
                </small>
              )}
            </div>
          </div>
        </div>
      </div>

      <Controller
        name="userPicture"
        control={control}
        render={({ field }) => (
          <PhotoUpload src={userPictureUrl || ""} onChange={field.onChange} />
        )}
      />
      <Divider />

      <div className="form-row">
        <div className="form-group">
          <label
            htmlFor="jobTitle"
            style={{
              color: validationErrors.includes("jobTitle")
                ? "#ef4444"
                : undefined,
            }}
          >
            Job title
          </label>
          <Controller
            name="jobTitle"
            control={control}
            rules={{ required: "Job title is required" }}
            render={({ field }) => (
              <InputText
                id="jobTitle"
                {...field}
                className={`w-full ${errors.jobTitle ? "p-invalid" : ""}`}
                placeholder="Job title"
              />
            )}
          />
          {errors.jobTitle && (
            <small
              className="p-error"
              style={{ display: "block", marginTop: "0.5rem" }}
            >
              {errors.jobTitle.message}
            </small>
          )}
        </div>
      </div>
      <Divider />

      <div className="form-row">
        <div className="form-group">
          <label>Country and city (optional)</label>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1rem",
              width: "100%",
            }}
          >
            <div style={{ width: "100%" }}>
              <Controller
                name="country"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    value={field.value}
                    onChange={(e) => field.onChange(e.value)}
                    options={countries}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select a country"
                    itemTemplate={countryTemplate}
                    valueTemplate={countryTemplate}
                    panelClassName="country-select-panel"
                    className="personal-info-country-dropdown"
                    style={{ width: "100%" }}
                    dropdownIcon="pi pi-chevron-down"
                  />
                )}
              />
            </div>
            <div style={{ width: "100%" }}>
              <Controller
                name="city"
                control={control}
                render={({ field }) => (
                  <InputText
                    {...field}
                    placeholder="City"
                    style={{ width: "100%", height: "52px" }}
                  />
                )}
              />
            </div>
          </div>
        </div>
      </div>
      <Divider />

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="phoneNumber">Phone number (optional)</label>
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field }) => (
              <div className="p-inputgroup">
                <div className="p-inputgroup-addon">
                  <Dropdown
                    value={selectedCountryCode}
                    options={countryPhoneCodes}
                    onChange={(e) => {
                      // console.log("Dropdown onChange:", e.value);
                      setSelectedCountryCode(e.value);
                      const selectedCountry = countryPhoneCodes.find(
                        (country) => country.value === e.value,
                      );
                      if (selectedCountry) {
                        const currentPhone = field.value || "";

                        // Remove existing country code if present
                        let cleanPhone = currentPhone;

                        // Check if the current phone starts with any known country code
                        const sortedCountryCodes = [...countryPhoneCodes].sort(
                          (a, b) => b.code.length - a.code.length,
                        );

                        for (const country of sortedCountryCodes) {
                          const codeWithoutPlus = country.code.replace(
                            /^\+/,
                            "",
                          );
                          if (
                            cleanPhone.startsWith(country.code) ||
                            cleanPhone.startsWith(codeWithoutPlus)
                          ) {
                            // Remove the matched country code
                            cleanPhone = cleanPhone
                              .replace(
                                new RegExp(`^\\+?${codeWithoutPlus}\\s*`),
                                "",
                              )
                              .trim();
                            break;
                          }
                        }

                        const newPhoneNumber =
                          `${selectedCountry.code} ${cleanPhone}`.trim();
                        field.onChange(newPhoneNumber);
                      }
                    }}
                    optionLabel="label"
                    optionValue="value"
                    className="country-code-dropdown"
                    showClear={false}
                    style={{
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                      borderRight: 0,
                    }}
                  />
                </div>
                <InputText
                  id="phoneNumber"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  placeholder="000 0000000"
                  style={{
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    borderLeft: 0,
                    padding: "1.5rem",
                  }}
                />
              </div>
            )}
          />
        </div>
      </div>
      <Divider />
    </>
  );
};
